import { useCallback, useEffect, useMemo, useState } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'

import { useQuery } from '@apollo/client/react'
import { clsx } from 'clsx'

import { useSplitEnabled } from '@buffer-mono/features'
import { useReferralProgramNavigation } from '~publish/hooks/useReferralProgramNavigation'
import { getUrl } from '@buffer-mono/shared-utils/bibe'
import { fetchStatusIncidents } from '@buffer-mono/shared-utils/status'
import { getOidcLogoutUrl } from '@buffer-mono/shared-utils/urls'
import {
  AllChannelsIcon,
  Avatar,
  Badge,
  BugIcon,
  XIcon,
  CodeXmlIcon,
  CircleDollarSignIcon,
  DropdownMenu,
  Flex,
  FlaskConicalIcon,
  HandshakeIcon,
  CircleHelpIcon,
  LightbulbIcon,
  LayoutGridIcon,
  LogOutIcon,
  MessageCircleHeartIcon,
  GiftIcon,
  MessageCircleQuestionMarkIcon,
  NewBadge,
  SettingsIcon,
  Sidebar,
  SparklesIcon,
  StatusDot,
  Text,
  VisuallyHidden,
  useSidebar,
  KeyboardIcon,
  KeyRoundIcon,
  Tooltip,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
  Product,
} from '@buffer-mono/tracking-plan'

import {
  CopyIdMenuItem,
  OpenInHQMenuItem,
  useShouldShowDeveloperData,
} from '~publish/components/DeveloperData'
import {
  useAccount,
  useHasOrganizationAction,
  usePlan,
} from '~publish/contexts/AccountContext'
import {
  CALLBACKS_PRODUCTION_URL,
  OIDC_IMPERSONATE_STOP_PATH,
} from '~publish/helpers/oidcAuth'
import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'
import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'
import { useKeyboardShortcut } from '~publish/hooks/useKeyboardShortcut'
import {
  settingsChannelsRoute,
  settingsBillingRoute,
  betaFeaturesRoute,
  referAFriendRoute,
  settings,
  settingsDeveloperRoute,
} from '~publish/legacy/routes'
import {
  MockedRibbon,
  MocktailConfigDialog,
  useIsMocktailActive,
  useShouldShowMocktail,
} from '~publish/mocktail'
import { usePersonalAccessTokens } from '~publish/pages/Settings/DeveloperDashboard/hooks'

import { PlanCTA } from './components/PlanCTA'
import { InternalToolsSubMenu } from './InternalToolsSubMenu'
import { OrgSwitcherCoachmark } from '../OrgSwitcherCoachmark'
import { KeyboardShortcutsDialog } from './KeyboardShortcutsDialog'
import { useMobileSidebarSafe } from '../MobileSidebar/MobileSidebarContext'
import { OrganizationSwitcher } from '../OrganizationSwitcher/OrganizationSwitcher'
import { GetAccountWithOrganizations } from '../OrganizationSwitcher/useOrganizationSwitcher'

import styles from './UserMenu.module.css'

const handleWhatsNewClick = (): void => {
  // Use the nuqs-powered opener for proper state sync
  window.__openChangelog?.()
}

/**
 * Helper to get a cookie value by key
 */
function getCookie(key: string): string | null {
  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const [cookieKey, cookieValue] = cookie.trim().split('=')
    if (cookieKey === key) {
      return decodeURIComponent(cookieValue)
    }
  }
  return null
}

/**
 * Developer Data submenu with Organization ID, Account ID, GraphQL Token, and Retool link
 */
function DeveloperDataSubMenu({
  organizationId,
  accountId,
}: {
  organizationId: string
  accountId?: string
}): JSX.Element {
  const token = getCookie('local_buffer_session')

  return (
    <>
      <DropdownMenu.Separator />
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger>
          <BugIcon size="small" />
          Developer Data
        </DropdownMenu.SubTrigger>
        <DropdownMenu.SubMenu>
          <CopyIdMenuItem
            label="Organization ID"
            toastMessage="Organization ID copied to clipboard!"
            id={organizationId}
          />
          {accountId && (
            <CopyIdMenuItem
              label="Account ID"
              toastMessage="Account ID copied to clipboard!"
              id={accountId}
            />
          )}
          {token && (
            <CopyIdMenuItem
              label="GraphQL Token"
              toastMessage="GraphQL Token copied to clipboard!"
              id={token}
            />
          )}
          <OpenInHQMenuItem
            label="Open Org in HQ"
            type="organization"
            id={organizationId}
          />
        </DropdownMenu.SubMenu>
      </DropdownMenu.Sub>
    </>
  )
}

type SubMenuItem = {
  id: string
  title: string
  url: string
  cta: string
  badge?: JSX.Element
  icon?: JSX.Element
}

type MenuItem = {
  id: string
  title: string
  icon: JSX.Element
  badge?: JSX.Element
  route?: string
  /** Omitted for submenu triggers: opening a submenu is not tracked. */
  cta?: string
  upgradePathName?: string
  hasDivider?: boolean
  hasSubMenu?: boolean
  subItems?: SubMenuItem[]
}

const UTM_PARAMS = 'utm_source=app&utm_medium=publish&utm_campaign=publish'

// The API item's New badge has no end date yet. While that badge is visible the
// expired-token dot stays hidden, so the item never shows two markers at once.
const showApiNewBadge: boolean = true

// Apps & Integrations submenu items
const appsAndIntegrationsItems: SubMenuItem[] = [
  {
    id: 'buffer-ios',
    title: 'Buffer for iOS',
    url: 'https://apps.apple.com/app/apple-store/id490474324?pt=936146&ct=TopBarAppsDropdown&mt=8',
    cta: 'publish-userMenu-apps-bufferIOS-1',
  },
  {
    id: 'buffer-android',
    title: 'Buffer for Android',
    url: 'https://play.google.com/store/apps/details?id=org.buffer.android',
    cta: 'publish-userMenu-apps-bufferAndroid-1',
  },
  {
    id: 'browser-extensions',
    title: 'Browser Extensions',
    url: 'https://buffer.com/extensions',
    cta: 'publish-userMenu-apps-browserExtensions-1',
  },
  {
    id: 'zapier-integration',
    title: 'Zapier Integration',
    url: 'https://zapier.com/apps/buffer/integrations',
    cta: 'publish-userMenu-apps-zapier-1',
  },
  {
    id: 'integrations',
    title: 'Integrations',
    url: '/settings/integrations',
    cta: 'publish-userMenu-apps-integrations-1',
    badge: <NewBadge className={styles.betaBadge} />,
  },
]

function ImpersonationWrapper({
  isImpersonating,
  children,
}: {
  isImpersonating?: boolean
  children: ReactNode
}): JSX.Element {
  if (!isImpersonating) {
    return <>{children}</>
  }

  return (
    <Flex direction="column" gap="xs" fullWidth>
      {children}
      <Tooltip content="You are impersonating another user">
        <div
          aria-label="You are impersonating a user"
          className={styles.impersonating}
        >
          <Flex align="center" gap="2xs">
            <KeyRoundIcon size="small" className={styles.impersonatingIcon} />
            <span className={styles.impersonatingText}>Impersonating</span>
          </Flex>
        </div>
      </Tooltip>
    </Flex>
  )
}

function AccountAvatar({ src }: { src?: string | null }): JSX.Element {
  const isMocktailActive = useIsMocktailActive()

  return (
    <span className={styles.avatarWrapper}>
      <Avatar src={src ?? undefined} size="small" alt="" />
      {isMocktailActive && <MockedRibbon />}
    </span>
  )
}

export function UserMenu(): JSX.Element {
  const { account } = useAccount()
  const { state } = useSidebar()
  const navigate = useNavigate()
  const [hasActiveIncident, setHasActiveIncident] = useState(false)
  const shouldShowDeveloperData = useShouldShowDeveloperData()
  const mobileSidebar = useMobileSidebarSafe()
  const { isEnabled: isBufferTeam, isReady: isBufferTeamReady } =
    useSplitEnabled('is-buffer-team')
  const shouldShowMocktail = useShouldShowMocktail()

  const { isEnabled: affiliateProgramEnabled } = useSplitEnabled(
    'dub-affiliate-program',
  )

  const referralProgram = useSplitEnabled('geid-referral-program')
  const referralNavigation = useReferralProgramNavigation({
    isReady: referralProgram.isReady,
    isEnabled: referralProgram.isEnabled,
    affiliateProgramEnabled,
  })
  const showReferralProgram = referralNavigation !== 'legacy'

  // Shares the `tfaBanner` dismissal key with the Settings > Profile 2FA
  // notice, so dismissing that notice also hides this badge.
  const twoFactorNudge = useDismissableBanner('tfaBanner')
  const showTwoFactorBadge = !account?.tfa?.type && twoFactorNudge.isActive

  // The OIDC logout endpoint clears the OIDC cookies when present and destroys the legacy session if one exists.
  const logoutUrl = getOidcLogoutUrl()

  // Prefetch organizations data on mount so it's ready when dropdown opens
  useQuery(GetAccountWithOrganizations)

  const { hasExpiredToken } = usePersonalAccessTokens()

  const canManageChannels = useHasOrganizationAction('manageChannels')
  const canManageBilling = useHasOrganizationAction('manageBilling')

  const isBetaEnabled = account?.currentOrganization?.isBetaEnabled
  // Stop always routes through the callback service; it ends an OIDC
  // impersonation directly and falls back to legacy admin for legacy sessions.
  const stopImpersonationUrl = getUrl(
    `${CALLBACKS_PRODUCTION_URL}${OIDC_IMPERSONATE_STOP_PATH}`,
  )

  const isSidebarCollapsed = state === 'collapsed'

  const plan = usePlan()
  const planName = plan.id
  const commonTrackingProperties = useCommonTrackingProperties()

  const trackCTAClicked = useCallback(
    (cta: string, upgradePathName: string | null = null): void => {
      BufferTracker.cTAClicked({
        ...commonTrackingProperties,
        product: Product.Publish,
        clientName: Client.PublishWeb,
        cta,
        upgradePathName,
      })
    },
    [commonTrackingProperties],
  )

  // Fetch status incidents on mount
  useEffect((): void => {
    fetchStatusIncidents()
      .then((incidents) => {
        setHasActiveIncident(incidents.length > 0)
        return undefined
      })
      .catch(() => undefined)
  }, [])

  // Help & Support submenu items (dynamic based on incident status)
  const helpAndSupportItems: SubMenuItem[] = useMemo(
    () => [
      {
        id: 'help-center',
        title: 'Help Center',
        url: `https://support.buffer.com/?${UTM_PARAMS}`,
        cta: 'publish-userMenu-help-helpCenter-1',
        icon: <MessageCircleQuestionMarkIcon size="small" />,
      },
      {
        id: 'suggest-feature',
        title: 'Suggest a Feature',
        url: 'https://suggestions.buffer.com',
        cta: 'publish-userMenu-help-suggestFeature-1',
        icon: <LightbulbIcon size="small" />,
      },
    ],
    [],
  )

  const [isShortcutsDialogOpen, setIsShortcutsDialogOpen] = useState(false)
  const handleKeyboardShortcutsClick = useCallback((): void => {
    trackCTAClicked('publish-userMenu-help-keyboardShortcuts-1')
    setIsShortcutsDialogOpen(true)
  }, [trackCTAClicked])

  const [isMocktailOpen, setIsMocktailOpen] = useState(false)
  const handleMocktailSelect = useCallback((): void => {
    setIsMocktailOpen(true)
  }, [])

  const toggleShortcutsDialog = useCallback(
    (): void => setIsShortcutsDialogOpen((prev: boolean): boolean => !prev),
    [],
  )
  useKeyboardShortcut('mod+/', toggleShortcutsDialog)

  // Raw handler for `?` — the useKeyboardShortcut hook does exact modifier
  // matching, and `?` inherently sets event.shiftKey on QWERTY keyboards.
  useEffect(() => {
    const handleQuestionMark = (event: KeyboardEvent): void => {
      if (event.key !== '?') return
      if (event.metaKey || event.ctrlKey || event.altKey) return

      const target = event.target
      if (
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return
      }

      event.preventDefault()
      setIsShortcutsDialogOpen((prev: boolean): boolean => !prev)
    }

    window.addEventListener('keydown', handleQuestionMark)
    return (): void => window.removeEventListener('keydown', handleQuestionMark)
  }, [])

  // App Status item (separate from help items, shown after divider)
  const appStatusItem: SubMenuItem = useMemo(
    () => ({
      id: 'status',
      title: hasActiveIncident ? 'Active Incident' : 'All Systems Operational',
      url: `https://status.buffer.com/?${UTM_PARAMS}`,
      cta: 'publish-userMenu-help-appStatus-1',
    }),
    [hasActiveIncident],
  )

  // Menu items for basic Settings
  const menuItems: MenuItem[] = useMemo(
    () => [
      {
        id: 'settings',
        title: 'Settings',
        icon: <SettingsIcon size="small" />,
        route: settings.route,
        cta: 'publish-userMenu-settings-1',
        hasDivider: false,
        badge: showTwoFactorBadge ? (
          <Tooltip content="Set up two-factor authentication">
            <span className={styles.twoFactorBadge}>
              <StatusDot data-status="warning" className={styles.statusDot} />
              <VisuallyHidden>Set up two-factor authentication</VisuallyHidden>
            </span>
          </Tooltip>
        ) : undefined,
      },
      ...(canManageChannels
        ? [
            {
              id: 'channels',
              title: 'Channels',
              icon: <AllChannelsIcon size="small" />,
              route: settingsChannelsRoute.route,
              cta: 'publish-userMenu-channels-1',
            },
          ]
        : []),
      ...(canManageBilling
        ? [
            {
              id: 'billing',
              title: 'Plans and Billing',
              icon: <CircleDollarSignIcon size="small" />,
              route: settingsBillingRoute.route,
              cta: 'navBar-dropdown-plansAndPricing-1',
              upgradePathName: 'mainNavMenu-trial',
            },
          ]
        : []),
      {
        id: 'help',
        title: 'Help & Support',
        icon: <CircleHelpIcon size="small" />,
        hasSubMenu: true,
        subItems: helpAndSupportItems,
      },
      {
        id: 'api',
        title: 'API',
        icon: <CodeXmlIcon size="small" />,
        route: settingsDeveloperRoute.route,
        cta: 'publish-userMenu-api-1',
        badge: (
          <>
            {hasExpiredToken && !showApiNewBadge && (
              <>
                <StatusDot className={styles.expiredTokenDot} />
                <VisuallyHidden>Token expired</VisuallyHidden>
              </>
            )}
            {showApiNewBadge && <NewBadge className={styles.betaBadge} />}
          </>
        ),
        hasDivider: true,
      },
      {
        id: 'apps-integrations',
        title: 'Apps & Integrations',
        icon: <LayoutGridIcon size="small" />,
        subItems: appsAndIntegrationsItems,
        hasSubMenu: true,
      },
      {
        id: 'beta',
        title: 'Beta Features',
        icon: <FlaskConicalIcon size="small" />,
        route: betaFeaturesRoute.route,
        cta: 'publish-userMenu-betaFeatures-1',
        hasDivider: false,
      },
      affiliateProgramEnabled && !showReferralProgram
        ? {
            id: 'partner-with-buffer',
            title: 'Partner Program',
            icon: <HandshakeIcon size="small" />,
            route: '/settings/partner-with-buffer',
            cta: 'publish-userMenu-partnerProgram-1',
          }
        : {
            id: 'refer-a-friend',
            title: 'Refer a Friend',
            icon: showReferralProgram ? (
              <GiftIcon size="small" />
            ) : (
              <MessageCircleHeartIcon size="small" />
            ),
            route: referAFriendRoute.route,
            cta: 'publish-userMenu-referAFriend-1',
          },
    ],
    [
      helpAndSupportItems,
      hasExpiredToken,
      affiliateProgramEnabled,
      showReferralProgram,
      canManageChannels,
      canManageBilling,
      showTwoFactorBadge,
    ],
  )

  const handleMenuItemClick = (item: MenuItem): void => {
    if (item.cta) {
      trackCTAClicked(item.cta, item.upgradePathName ?? null)
    }

    // On mobile, open the settings drawer instead of navigating
    if (item.id === 'settings' && mobileSidebar) {
      mobileSidebar.setSettingsOpen(true)
      return
    }

    if (item.route) {
      navigate(item.route)
    }
  }

  return (
    <ImpersonationWrapper isImpersonating={account?.isImpersonation}>
      <OrgSwitcherCoachmark>
        <DropdownMenu modal={false}>
          <DropdownMenu.Trigger asChild>
            <Sidebar.Button
              className={clsx(
                styles.userMenu,
                isSidebarCollapsed && styles.collapsed,
              )}
              prefix={<AccountAvatar src={account?.avatar} />}
              aria-label={account?.currentOrganization?.name}
            >
              <Flex direction="column" className={styles.userInfo} fullWidth>
                <Text
                  weight="medium"
                  className={styles.userName}
                  translate="no"
                >
                  {account?.currentOrganization?.name}
                </Text>
                <Text size="sm" color="subtle" className={styles.usePlanName}>
                  {planName} plan
                </Text>
              </Flex>
            </Sidebar.Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content className={styles.dropdownContent}>
            <div className={styles.scrollableItems}>
              <OrganizationSwitcher />
              <PlanCTA />
              <DropdownMenu.Separator />
              {menuItems.map((item) => (
                <div key={item.id}>
                  {item.hasDivider && <DropdownMenu.Separator />}
                  {item.hasSubMenu && item.subItems ? (
                    <DropdownMenu.Sub>
                      <DropdownMenu.SubTrigger>
                        {item.icon}
                        {item.title}
                        {item.badge}
                      </DropdownMenu.SubTrigger>
                      <DropdownMenu.SubMenu className={styles.subMenuContent}>
                        {item.subItems.map((subItem) => {
                          const isInternalRoute = subItem.url.startsWith('/')

                          return (
                            <DropdownMenu.Item
                              key={subItem.id}
                              asChild
                              onSelect={() => trackCTAClicked(subItem.cta)}
                            >
                              {isInternalRoute ? (
                                <NavLink to={subItem.url}>
                                  {subItem.icon}
                                  {subItem.title}
                                  {subItem.badge}
                                </NavLink>
                              ) : (
                                <a
                                  href={subItem.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {subItem.icon}
                                  {subItem.title}
                                  {subItem.badge}
                                </a>
                              )}
                            </DropdownMenu.Item>
                          )
                        })}
                        {item.id === 'help' && (
                          <DropdownMenu.Item
                            onSelect={() => {
                              trackCTAClicked(
                                'publish-userMenu-help-whatsNew-1',
                              )
                              handleWhatsNewClick()
                            }}
                          >
                            <SparklesIcon size="small" />
                            What&apos;s New
                          </DropdownMenu.Item>
                        )}
                        {item.id === 'help' && (
                          <DropdownMenu.Item
                            onSelect={handleKeyboardShortcutsClick}
                          >
                            <KeyboardIcon size="small" />
                            Keyboard Shortcuts
                          </DropdownMenu.Item>
                        )}
                        {item.id === 'help' && (
                          <>
                            <DropdownMenu.Separator />
                            <DropdownMenu.Group label="App Status">
                              <DropdownMenu.Item
                                asChild
                                onSelect={() =>
                                  trackCTAClicked(appStatusItem.cta)
                                }
                              >
                                <a
                                  href={appStatusItem.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.appStatusItem}
                                  data-status={
                                    hasActiveIncident ? 'warning' : 'success'
                                  }
                                >
                                  <StatusDot
                                    data-status={
                                      hasActiveIncident ? 'warning' : 'success'
                                    }
                                    className={styles.statusDot}
                                  />
                                  {appStatusItem.title}
                                </a>
                              </DropdownMenu.Item>
                            </DropdownMenu.Group>
                          </>
                        )}
                      </DropdownMenu.SubMenu>
                    </DropdownMenu.Sub>
                  ) : (
                    <DropdownMenu.Item
                      onSelect={() => handleMenuItemClick(item)}
                    >
                      {item.icon}
                      {item.title}
                      {item.badge}
                      {item.id === 'beta' && (
                        <Badge
                          size="small"
                          variant="beta"
                          className={styles.betaBadge}
                        >
                          {isBetaEnabled ? 'On' : 'Off'}
                        </Badge>
                      )}
                    </DropdownMenu.Item>
                  )}
                </div>
              ))}
              {shouldShowDeveloperData && account?.currentOrganization?.id && (
                <DeveloperDataSubMenu
                  organizationId={account.currentOrganization.id}
                  accountId={account.id}
                />
              )}
              {isBufferTeamReady && isBufferTeam && (
                <InternalToolsSubMenu
                  subMenuClassName={styles.subMenuContent}
                  onSelectMocktail={handleMocktailSelect}
                />
              )}
              <DropdownMenu.Separator />
              {account?.isImpersonation && stopImpersonationUrl ? (
                <DropdownMenu.Item
                  asChild
                  className={styles.stopImpersonationItem}
                >
                  <a href={stopImpersonationUrl} rel="noopener noreferrer">
                    <XIcon size="small" />
                    Stop Impersonation
                  </a>
                </DropdownMenu.Item>
              ) : (
                <DropdownMenu.Item
                  asChild
                  onSelect={() => trackCTAClicked('publish-userMenu-logout-1')}
                >
                  <a href={logoutUrl} rel="noopener noreferrer">
                    <LogOutIcon size="small" />
                    Log out
                  </a>
                </DropdownMenu.Item>
              )}
            </div>
          </DropdownMenu.Content>
        </DropdownMenu>
      </OrgSwitcherCoachmark>
      <KeyboardShortcutsDialog
        open={isShortcutsDialogOpen}
        onOpenChange={setIsShortcutsDialogOpen}
      />
      {isBufferTeamReady && isBufferTeam && shouldShowMocktail && (
        <MocktailConfigDialog
          open={isMocktailOpen}
          onOpenChange={setIsMocktailOpen}
        />
      )}
    </ImpersonationWrapper>
  )
}
